// src/components/admin/control-tower/SearchBar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Search, X, MapPin, Package, User } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

function SearchBar({ activeView, onResultClick }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search - triggers when searchTerm or activeView changes
  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      if (searchTerm.trim().length > 0) {
        await performSearch(searchTerm);
      } else {
        setResults([]);
        setIsOpen(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, activeView]);

  async function performSearch(term) {
    setIsLoading(true);
    try {
      let endpoint = '';
      
      // Determine endpoint based on active view
      switch (activeView) {
        case 'deliveries':
          endpoint = `${API_URL}/api/admin/search-deliveries`;
          break;
        case 'orders':
          endpoint = `${API_URL}/api/admin/search-orders`;
          break;
        case 'users':
          endpoint = `${API_URL}/api/admin/search-users`;
          break;
        default:
          endpoint = `${API_URL}/api/admin/search-deliveries`;
      }

      const response = await axios.get(endpoint, {
        params: { q: term, limit: 5 },
      });

      setResults(response.data.results || []);
      setIsOpen(true);
    } catch (error) {
      console.error('Error searching:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }

  function handleResultClick(id) {
    // Determine type based on active view
    let type = 'delivery';
    
    switch (activeView) {
      case 'deliveries':
        type = 'delivery';
        break;
      case 'orders':
        type = 'order';
        break;
      case 'users':
        type = 'user';
        break;
    }
    
    onResultClick(id, type);
    setSearchTerm('');
    setResults([]);
    setIsOpen(false);
  }

  function clearSearch() {
    setSearchTerm('');
    setResults([]);
    setIsOpen(false);
  }

  function getPlaceholder() {
    switch (activeView) {
      case 'deliveries':
        return 'Buscar entrega...';
      case 'orders':
        return 'Buscar recojo...';
      case 'users':
        return 'Buscar usuario...';
      default:
        return 'Buscar...';
    }
  }

  function getIcon() {
    switch (activeView) {
      case 'deliveries':
        return <MapPin className="w-4 h-4 text-[#B1336E]" />;
      case 'orders':
        return <Package className="w-4 h-4 text-[#E6B43C]" />;
      case 'users':
        return <User className="w-4 h-4 text-[#67A43C]" />;
      default:
        return <MapPin className="w-4 h-4 text-[#B1336E]" />;
    }
  }

  function getRoleBadgeColor(role) {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'bg-purple-100 text-purple-700';
      case 'master':
        return 'bg-amber-100 text-amber-800';
      case 'driver':
        return 'bg-blue-100 text-blue-700';
      case 'user':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  }

  function getRoleLabel(role) {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'Admin';
      case 'master':
        return 'Master';
      case 'driver':
        return 'Conductor';
      case 'user':
        return 'Cliente';
      default:
        return role || 'Usuario';
    }
  }

  return (
    <div ref={searchRef} className="relative w-full lg:w-auto">
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={getPlaceholder()}
          className="w-full lg:w-64 pl-9 pr-8 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#B1336E] focus:border-transparent font-montserrat"
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        {searchTerm && (
          <button
            onClick={clearSearch}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute inset-x-0 top-full mt-2 max-h-[min(20rem,55dvh)] overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg z-[60]">
          {isLoading ? (
            <div className="p-4 text-center text-slate-500 text-sm font-montserrat">
              Buscando...
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-slate-500 text-sm font-montserrat">
              No se encontraron resultados
            </div>
          ) : (
            <div className="py-2">
              {results.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleResultClick(item.id)}
                  className="w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0"
                >
                  {activeView === 'users' ? (
                    // User result layout
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        {item.profile_image ? (
                          <img
                            src={item.profile_image}
                            alt={item.username}
                            className="w-10 h-10 rounded-full object-cover border-2 border-slate-200"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#B1336E] to-[#E6B43C] flex items-center justify-center">
                            <span className="text-white font-bold text-sm font-montserrat">
                              {item.username?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-slate-800 text-sm font-montserrat truncate">
                            {item.username}
                          </span>
                          {item.role && (
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium font-montserrat ${getRoleBadgeColor(item.role)}`}>
                              {getRoleLabel(item.role)}
                            </span>
                          )}
                        </div>
                        {item.email && (
                          <div className="text-xs text-slate-500 truncate font-montserrat">
                            📧 {item.email}
                          </div>
                        )}
                        {item.phone && (
                          <div className="text-xs text-slate-500 mt-0.5 font-montserrat">
                            📱 {item.phone}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    // Delivery/Order result layout
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        {getIcon()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-[#B1336E] text-sm font-montserrat">
                            #{item.id}
                          </span>
                          {item.status && (
                            <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-montserrat">
                              {item.status}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-700 font-medium truncate font-montserrat">
                          {item.name}
                        </div>
                        {item.phone && (
                          <div className="text-xs text-slate-500 mt-0.5 font-montserrat">
                            {item.phone}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchBar;
